import { isEraId } from '../config/eras';
import type { LapRecord } from '../game/replay';
import { decodeInputs } from '../sim/input';
import { isProfane } from './profanity';

/**
 * The leaderboard's wire format and rules, shared by the game and the server
 * (worker/). A day's board is every player's best lap on that day's circuit
 * with that day's car: (date, trackHash, physicsHash). Every honest client
 * computes the same hashes, so the server never has to rebuild the circuit.
 */

export const NAME_MAX = 20;

/**
 * A day whose car changed mid-race keeps one board: laps with the new car are kept under the car the day
 * began with, so the times already set stay on it (each lap still says which car it was driven with, so it
 * replays with that one). 6 October 2026: handling 3 came in during the 1932 car's race; 12 October 2026: handling
 * 3.4 during the 2004 car's.
 * By date: the new car's hash -> the board's.
 */
export const BOARD_KEYS: Record<string, Record<string, string>> = {
  '2026-10-06': { '55214e24038680f0': '619cafb83c8a789c' },
  '2026-10-12': { cdbfbb0cf8d9d643: '70052269347a3b96' },
};

/** Days whose first car no longer counts (12 October 2026: the 2004 before handling 3.4, its laps taken off the board). */
const FIRST_CAR_OFF = new Set(['2026-10-12']);

/** The board a lap with this car goes on, that day. */
export function boardKey(date: string, physicsHash: string): string {
  return BOARD_KEYS[date]?.[physicsHash] ?? physicsHash;
}

/** Control, zero-width and bidi-override characters, and angle brackets: nothing a name should carry. */
function hidden(c: number): boolean {
  return (
    c < 0x20 ||
    (c >= 0x7f && c <= 0x9f) ||
    (c >= 0x200b && c <= 0x200f) ||
    (c >= 0x2028 && c <= 0x202e) ||
    (c >= 0x2060 && c <= 0x206f) ||
    c === 0xfeff ||
    c === 0x3c ||
    c === 0x3e
  );
}

/** A player's name as it will be shown: trimmed, single-spaced, no hidden characters. '' if nothing is left. */
export function cleanName(raw: string): string {
  const visible = Array.from(raw.normalize('NFC'))
    .filter((ch) => !hidden(ch.codePointAt(0) ?? 0))
    .join('');
  return Array.from(visible.replace(/\s+/g, ' ').trim())
    .slice(0, NAME_MAX)
    .join('')
    .trim();
}

export const PLAYER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const HASH = /^[0-9a-f]{16}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const BASE64 = /^[A-Za-z0-9+/=_-]*$/;
/** A lap's inputs are run-length encoded; even a slow, busy lap is a few kilobytes. */
const INPUTS_MAX = 120_000;

export interface SubmitBody {
  playerId: string;
  name: string;
  era: string;
  record: LapRecord;
  /**
   * The friends' links this player raced today (their ids, a few at most): racing a friend's link and setting a
   * lap makes a challenge between the two of you for the day (battles, worker/index.ts).
   */
  via?: string[];
  /**
   * Raced inside a Discord server, with the player's posts on: the lap's message goes out there when they leave
   * (worker/discordPosts.ts draws its picture). The server, the channel the game was opened in (when Discord said),
   * and the player's own link ("Race this lap" opens it).
   */
  discord?: DiscordPost;
  /** Set inside Discord (a server, a chat or a DM): the board shows Discord's logo by the name. */
  inDiscord?: boolean;
}

export interface DiscordPost {
  guild: string;
  channel?: string;
  link?: string;
}

/** A Discord id (a snowflake). */
export const SNOWFLAKE = /^\d{5,25}$/;

/** At most this many links' challenges go with a lap. */
export const VIA_MAX = 5;

export interface BoardEntry {
  rank: number;
  name: string;
  lapMs: number;
  /** Behind the leader, ms (0 for the leader). */
  gapMs: number;
  you: boolean;
  /** A public handle on the lap, to watch it (its time and when it was set; never the player's id). */
  ref?: string;
  /** Someone you have a challenge with today: their lap is yours to race, even if it leads. */
  rival?: boolean;
  /** The lap was set inside Discord, or signed in with Discord on the website: Discord's logo by the name. */
  discord?: boolean;
}

/**
 * A battle's news: someone you have a challenge with today (one of you raced the other's link) whose lap is ahead
 * of yours, to race and take it back.
 */
export interface Battle {
  name: string;
  lapMs: number;
  /** How far ahead of your lap, ms. */
  gapMs: number;
  /** Their lap's handle (yours to race, even if it leads). */
  ref: string;
  /** Their lap leads the day. */
  leads: boolean;
}

export interface Board {
  date: string;
  /** The day is over: the board is final and takes no more laps. */
  closed: boolean;
  total: number;
  /** The top of the board. */
  entries: BoardEntry[];
  /** The asking player's own row, wherever it is. */
  you: BoardEntry | null;
  /** The asking player's next target: the lap one place ahead of theirs (never the leader's), the one other lap they may watch. */
  target?: BoardEntry | null;
  /** Battles: the laps of those you have a challenge with today that are ahead of yours, newest first. */
  battles?: Battle[];
  /** Made-up laps caught on this board today. */
  caught?: Caught[];
}

export interface SubmitResponse {
  accepted: boolean;
  reason?: string;
  /** A made-up lap sent again: what the stewards say to it (net/stewards.ts). The game doesn't show it. */
  note?: string;
  /** This lap beat the player's previous time on the board. */
  improved?: boolean;
  rank?: number;
  total?: number;
  bestMs?: number;
}

/** A board lap's public handle: "lapMs.updatedAt". */
export const LAP_REF = /^\d{4,6}\.\d{10,16}$/;

/** A shared lap's link id (…/?lap=Ab3dE9xYz1). */
export const SHARE_ID = /^[A-Za-z0-9]{10}$/;

/** Sharing your lap as a link: the lap goes on the day's board under your name, and the link points at it. */
export interface ShareBody extends SubmitBody {
  id: string;
  /** The link's preview picture: a 1200 × 630 JPEG, base64. */
  image?: string;
}

/** A preview picture is a few tens of kilobytes; this is a generous ceiling (base64 characters). */
export const SHARE_IMAGE_MAX = 400_000;

/** A lap to watch and then race: a shared link's, a board row's, the day's winner's. */
export interface WatchLap {
  /** '' when it was shared without a name. */
  name: string;
  era: string;
  lapMs: number;
  record: LapRecord;
  /** Where the lap stands (or would stand) on its day's board. */
  rank: number | null;
  total: number | null;
  /** A link's: the day it was last sent (its driver's lap is today's, whenever it was sent). */
  sentOn?: string;
  /**
   * The champion's lap: its driver won the day before its day. On today's board, yesterday's winner (their car is
   * gold today, for everyone who races or watches it); on yesterday's, who raced in gold then (its podium watched again).
   */
  champion?: boolean;
}

/** A friend's link whose driver has no lap today (yet): who sent it, and when. */
export interface LinkNoLap {
  none: true;
  /** '' when not known. */
  name: string;
  sentOn: string;
}

/** A day's winner: the fastest lap on the day's main board (the one most drivers raced on). */
export interface Winner {
  date: string;
  name: string;
  lapMs: number;
  era: string;
  trackHash: string;
  physicsHash: string;
  ref: string;
  total: number;
}

/** A step of a day's podium. */
export interface PodiumPlace {
  /** 1, 2 or 3, in the podium's order (names the filter blocks are left off it, as on the winner's spotlight). */
  place: number;
  name: string;
  lapMs: number;
  /** The lap's public handle: every podium lap can be watched once its day is over. */
  ref: string;
}

/** A day's podium: the top three of its main board (the circuit and car most drivers raced). */
export interface Podium {
  date: string;
  era: string;
  trackHash: string;
  physicsHash: string;
  total: number;
  places: PodiumPlace[];
}

/**
 * The morning paper (GET /api/paper?player): yesterday's race, for one player, the day after. The front page
 * is the podium; `you` is the player's own column, or null if they weren't racing yesterday (then `away` says
 * whether they raced in the week before, for the paper that misses them).
 */
export interface Paper {
  /** The race reported: yesterday. */
  date: string;
  era: string;
  /** Drivers on yesterday's main board. */
  total: number;
  podium: { place: number; name: string; lapMs: number }[];
  you: PaperColumn | null;
  away: boolean;
  /** Days in a row with a lap, up to and including yesterday (0 when not yesterday). */
  streak: number;
  /** Yesterday's made-up laps the stewards caught: the corner of shame. */
  caught?: Caught[];
}

/**
 * A made-up lap the stewards caught: driven again from its own inputs, the very same lap took longer than the
 * time it was sent with (its time was edited). One a lap, however many names and tries it came under.
 */
export interface Caught {
  /** The name it was first sent under; null when that name is another driver's (nobody gets shamed for a name taken). */
  name: string | null;
  /** The other names the same lap came under. */
  aka: string[];
  claimedMs: number;
  droveMs: number;
  /** Times it was sent. */
  tries: number;
}

/** A player's own column in the paper. */
export interface PaperColumn {
  name: string;
  rank: number;
  lapMs: number;
  /** Behind the place above (ms); null at the top. */
  nextGapMs: number | null;
  /** The place the day before, if they raced it on its main board. */
  prevRank: number | null;
  /** Better than any place they finished before (of the days kept), with at least one before it. */
  bestFinish: boolean;
  /** The closest of yesterday's link rivals who beat them, if any. */
  rival: { name: string; byMs: number } | null;
}

/**
 * A day's circuit as the server knows it, without building it: written next to each prebuilt circuit at
 * deploy time (circuits/<date>.meta.json, scripts/circuits.ts).
 */
export interface DayMeta {
  date: string;
  era: string;
  trackHash: string;
  physicsHash: string;
  /** The bot's lap (the author medal), ms. */
  authorMs: number;
}

/** Where a day's meta is published (next to its prebuilt circuit). */
export function metaPath(date: string): string {
  return `circuits/${date}.meta.json`;
}

/**
 * The fastest a lap may be against the bot's: real drivers have beaten it by 16% (31.485 against 37.592);
 * a lap under 65% of it isn't driving.
 */
export const FASTEST_VS_BOT = 0.65;

/**
 * Why a lap that passes the shape checks still can't be real, or null. Without its day's meta (no prebuilt
 * circuit for it) only the recording itself is checked.
 *  - It must be on the day's circuit with the day's car.
 *  - It can't be impossibly fast for that circuit.
 *  - Its recording must hold exactly one input per tick up to the lap's end, and that run can't be
 *    shorter than the lap.
 * Not a replay (that needs more CPU than the free plan gives): a made-up lap built with care still gets
 * through, and the stats page can take it off the board.
 */
export function checkPlausible(rec: LapRecord, era: string, meta: DayMeta | null): string | null {
  if (meta) {
    // The day's car, or (a day whose car changed mid-race) the one it began with: a game loaded before the
    // change can still save its laps, unless that car was taken off the day's board.
    const car = rec.physicsHash === meta.physicsHash || (rec.physicsHash === boardKey(meta.date, meta.physicsHash) && !FIRST_CAR_OFF.has(meta.date));
    if (rec.trackHash !== meta.trackHash || !car || era !== meta.era) return 'not today’s circuit · reload the game';
    if (rec.lapMs < meta.authorMs * FASTEST_VS_BOT) return 'lap not possible';
  }
  let ticks: number;
  try {
    ticks = decodeInputs(rec.inputs).length;
  } catch {
    return 'bad inputs';
  }
  if (ticks !== rec.endTick) return 'bad inputs';
  if ((rec.endTick * 1000) / 60 < rec.lapMs - 1) return 'lap not possible';
  return null;
}

/** Why the server's replay refuses a lap (net/verify.ts): also the answer when that lap comes again. */
export const NO_REPLAY = 'lap doesn’t replay';
/** A player's laps that didn't replay, in a day, before none of their laps are taken that day: one made-up time. */
export const REFUSED_MAX = 1;
export const TOO_MANY_REFUSED = 'too many laps that didn’t replay today';

/**
 * A lap refused today (refused_laps): the time it claimed, its last tick, and why. `other`: another player's,
 * the very same lap that didn't replay (else the player's own).
 */
export interface RefusedLap {
  lapMs: number;
  endTick: number;
  reason: string;
  other?: boolean | number;
}

/**
 * A lap from a player with laps refused today, or one another player had refused: why it isn't taken, with no
 * replay, or null. That very lap again (the game that sent it doesn't know, and sends it again): the reason it
 * had. After REFUSED_MAX of their own, none at all for the rest of the day: a time tweaked and sent again and
 * again gets nowhere, nor one placed just out of the replayed places (13 October 2026: made-up times just behind
 * the leader, after three caught leading). And the very lap that didn't replay for someone else: a script sent one
 * made-up lap a dozen times, each under a new player (14 October 2026).
 */
export function refusedAgain(refused: readonly RefusedLap[], rec: Pick<LapRecord, 'lapMs' | 'endTick'>): string | null {
  const same = refused.find((x) => x.lapMs === rec.lapMs && x.endTick === rec.endTick);
  if (same) return same.reason;
  return refused.filter((x) => !x.other).length >= REFUSED_MAX ? TOO_MANY_REFUSED : null;
}

/** Why a submission can't go on the board, or null if it can. */
export function checkSubmission(b: unknown): string | null {
  if (!b || typeof b !== 'object') return 'bad request';
  const s = b as Partial<SubmitBody>;
  if (typeof s.playerId !== 'string' || !PLAYER_ID.test(s.playerId)) return 'bad player id';
  if (typeof s.name !== 'string' || cleanName(s.name) === '') return 'name required';
  if (isProfane(cleanName(s.name))) return 'name not allowed';
  if (typeof s.era !== 'string' || !isEraId(s.era)) return 'unknown car';
  if (s.via !== undefined && (!Array.isArray(s.via) || s.via.length > VIA_MAX || !s.via.every((v) => typeof v === 'string' && SHARE_ID.test(v)))) return 'bad link';
  if (s.discord !== undefined && !isDiscordPost(s.discord)) return 'bad server';
  if (s.inDiscord !== undefined && typeof s.inDiscord !== 'boolean') return 'bad request';
  return checkRecord(s.record);
}

/**
 * Why a lap can't be shared as a link, or null if it can. A link points at the
 * lap on the day's board, so the board's rules apply (a name included).
 */
export function checkShare(b: unknown): string | null {
  if (!b || typeof b !== 'object') return 'bad request';
  const s = b as Partial<ShareBody>;
  if (typeof s.id !== 'string' || !SHARE_ID.test(s.id)) return 'bad link id';
  // A JPEG's base64 always starts "/9j/" (its first bytes are FF D8 FF).
  if (s.image !== undefined && (typeof s.image !== 'string' || s.image.length > SHARE_IMAGE_MAX || !s.image.startsWith('/9j/') || !/^[A-Za-z0-9+/]+={0,2}$/.test(s.image))) {
    return 'bad picture';
  }
  return checkSubmission(s);
}

function isDiscordPost(d: unknown): boolean {
  if (!d || typeof d !== 'object') return false;
  const p = d as Partial<DiscordPost>;
  return (
    typeof p.guild === 'string' &&
    SNOWFLAKE.test(p.guild) &&
    (p.channel === undefined || (typeof p.channel === 'string' && SNOWFLAKE.test(p.channel))) &&
    (p.link === undefined || (typeof p.link === 'string' && SHARE_ID.test(p.link)))
  );
}

function checkRecord(rec: unknown): string | null {
  const r = rec as Partial<LapRecord> | undefined;
  if (!r || typeof r !== 'object' || r.version !== 1) return 'bad lap';
  if (typeof r.date !== 'string' || !DATE.test(r.date)) return 'bad date';
  if (typeof r.trackHash !== 'string' || !HASH.test(r.trackHash)) return 'bad circuit';
  if (typeof r.physicsHash !== 'string' || !HASH.test(r.physicsHash)) return 'bad car';
  if (!Number.isInteger(r.lapMs) || (r.lapMs as number) < 10_000 || (r.lapMs as number) > 300_000) return 'bad lap time';
  const sec = r.sectorMs;
  if (!Array.isArray(sec) || sec.length !== 3 || !sec.every((v) => Number.isInteger(v) && v > 0)) return 'bad sectors';
  if (Math.abs(sec[0] + sec[1] + sec[2] - (r.lapMs as number)) > 3) return 'sectors do not add up';
  if (!Number.isInteger(r.endTick) || (r.endTick as number) < 1 || (r.endTick as number) > 60 * 600) return 'bad lap';
  if (!Number.isInteger(r.lapIndex) || (r.lapIndex as number) < 1) return 'bad lap';
  if (typeof r.inputs !== 'string' || r.inputs.length === 0 || r.inputs.length > INPUTS_MAX || !BASE64.test(r.inputs)) return 'bad inputs';
  return null;
}
